import { useState } from 'react';
import * as _ from 'lodash';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Button,
  Heading,
  Box,
  Flex,
  Icon,
  Tooltip,
  IconButton,
  Checkbox,
  Dropdown,
  TextField,
  Avatar,
  Badge,
  Label,
  Dialog,
  DialogContentContainer,
  Divider,
} from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';

import {
  Table,
  Menu,
  Drag,
  MoveArrowRight,
  Delete,
  Page,
  Column as ColumnIcon,
  Settings as SettingsIcon,
  Education,
  Filter,
  Board as BoardIcon,
  TurnInto,
  Status,
  Formula as FormulaIcon,
  Form as FormIcon,
  Note as NoteIcon,
  SettingsKnobs,
} from '@vibe/icons';

import {
  getBoardFieldId,
  getBoardFields,
  isBlankBoardField,
  isCustomFormulaBoardField,
} from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/spreadsheet-manager';
import {
  BoardField,
  Template,
  TemplateConfig,
  BoardColumn,
  FieldGroup as FieldGroupType,
  Sheet,
} from '@gorilla/spreadsheet-shared/src/lib/spreadsheet-manager/types';
import { BoardRulesModal } from '@gorilla/ui/src/components/BoardRulesModal';
import { BoardDiagnoseModal } from '@gorilla/ui/src/components/BoardDiagnoseModal';
import { Board } from '@gorilla/common/src/lib/monday-api/api';
import { hasBoardFilters, areBoardFiltersValid } from '@gorilla/common/src/lib/rules';
import { FieldPicker } from './FieldPicker';
import { ColumnConfigModal } from './ColumnConfigModal';
import { createRandomId, indexToAlpha } from '../lib/utils';
import { FormulaDocumentationModal } from './FormulaDocumentationModal';
import { ToMondayModal } from '../components/ToMondayModal';
import FieldGroupAdvancedSettingsModal from '../components/FieldGroupAdvancedSettingsModal';
import { createSignal } from '../services/signals';
import { mondayClient } from '../services/monday';
import S5SCalc from '@gorilla/spreadsheet-shared/src/lib/external/sjscalc.js';

function isValidFormula(formula: string) {
  if (!formula.length || formula[0] !== '=') {
    return true;
  }

  try {
    // remove first char of formula
    const formulaPrepared = (formula.substring(1) as any).replaceAll('$$', 1);
    const result = S5SCalc.calculate(formulaPrepared);

    return true;
  } catch (err: any) {
    // Cell references fail with a TypeError because there is no workbook here; syntax errors are thrown as strings.
    // Don't match on the message: its wording differs per browser.
    return err instanceof TypeError;
  }
}

const COLUMN_TYPE_OPTIONS = [
  {
    label: 'Board column',
    value: 'board-column',
    //leftIcon: NoteIcon,
  },
  {
    label: 'Custom formula',
    value: 'custom-formula',
    //leftIcon: FormIcon,
  },
  {
    label: 'Empty column',
    value: 'blank',
    //leftIcon: Page,
  },
];

type ColumnProps = {
  idx: number;
  fieldGroup: FieldGroupType;
  board: Board;
  template: Template;
  sheet: Sheet;

  fields: BoardField[];
  column: BoardColumn;
  onChange: (column: BoardColumn) => void;
  onRemove: () => void;
  onSave: () => void;
};

function Column({ idx, fields, column, onChange, onRemove, fieldGroup, board, template, sheet, onSave }: ColumnProps) {
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showToMondayModal, setShowToMondayModal] = useState<boolean>(false);
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const boardColumnFields = fields.filter((f) => !isBlankBoardField(f) && !isCustomFormulaBoardField(f));

  const boardFieldId = getBoardFieldId(column);
  const boardField = fields.find(({ id }) => id === boardFieldId);

  let customFormulaValidation: any = null;
  let selectedColumnTypeId = 'board-column';

  if (boardField && isBlankBoardField(boardField)) {
    selectedColumnTypeId = 'blank';
  } else if (boardField && isCustomFormulaBoardField(boardField)) {
    selectedColumnTypeId = 'custom-formula';

    if (!isValidFormula(column.config?.formula || '')) {
      customFormulaValidation = {
        status: 'error',
      };
    }
  }

  const selectedColumnTypeOption = COLUMN_TYPE_OPTIONS.find((option) => option.value === selectedColumnTypeId);
  let initialCtx: any = {};

  if (column.config?.to_monday_target_column_id) {
    initialCtx.targetColumnId = column.config.to_monday_target_column_id;
  }

  if (_.isBoolean(column.config?.to_monday_is_quick_actions)) {
    initialCtx.isQuickAction = column.config.to_monday_is_quick_actions;
  }

  if (column.config?.to_monday_quick_action_name) {
    initialCtx.quickActionName = column.config.to_monday_quick_action_name;
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {showToMondayModal ? (
        <ToMondayModal
          onClose={() => setShowToMondayModal(false)}
          show={true}
          initialCtx={initialCtx}
          targetBoard={board}
          template={template}
          formulaColumnIdx={idx}
          fieldGroup={fieldGroup}
          sheet={sheet}
          formula={column.config?.formula || ''}
          onSaveSettings={(toMondaySettings) => {
            const newConfig = column.config ? { ...column.config, ...toMondaySettings } : toMondaySettings;

            onChange({
              ...column,
              config: newConfig,
            });

            setTimeout(() => {
              onSave();
            }, 10);
          }}
        />
      ) : null}
      <div style={{ overflow: 'visible', marginBottom: '8px' }}>
        <Flex gap="large" align="center" direction="row">
          <div
            className="dragger"
            {...listeners}
            style={{
              lineHeight: 0,
            }}
          >
            <Icon icon={Drag} size={20} />
          </div>

          <div
            style={{
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                flexWrap: 'nowrap',
                gap: '7px',
                width: '100%',
              }}
            >
              <div style={{ width: '165px' }}>
                <Dropdown
                  searchable
                  onOptionSelect={(ev: any) => {
                    if (ev.value === 'blank') {
                      onChange({
                        ...column,
                        path: [{ type: 'field', board_id: fieldGroup.board_id, field_id: 'blank' }],
                        config: null,
                      });
                    } else if (ev.value === 'custom-formula') {
                      onChange({
                        ...column,
                        path: [{ type: 'field', board_id: fieldGroup.board_id, field_id: 'custom-formula' }],
                        config: null,
                      });
                    } else {
                      onChange({
                        ...column,
                        path: boardColumnFields[0].path,
                        config: null,
                      });
                    }
                  }}
                  value={dropdownValue(selectedColumnTypeOption)}
                  options={COLUMN_TYPE_OPTIONS}
                  placeholder="Select type"
                  clearable={false}
                  className="column-type-selector"
                  menuWrapperClassName="dropdown_content_indexed"
                />
              </div>
              <div style={{ flex: 1 }}>
                {selectedColumnTypeId === 'board-column' && (
                  <FieldPicker
                    mirrorColumnsDisabled={fieldGroup.disable_mirror_columns}
                    fields={boardColumnFields}
                    value={getBoardFieldId(column)}
                    onChange={(field) => {
                      onChange({
                        ...column,
                        path: field.path,
                        config: null,
                      });
                    }}
                  />
                )}
                {selectedColumnTypeId === 'custom-formula' && (
                  <Flex gap={8}>
                    <TextField
                      size="medium"
                      icon={Education}
                      className="formula--input"
                      placeholder="=SUM(A2:A10)"
                      onIconClick={() => {
                        setShowDocumentation(true);
                      }}
                      onChange={(newFormula) => {
                        onChange({
                          ...column,
                          config: {
                            ...(column?.config || {}),
                            formula: newFormula,
                          },
                        });
                      }}
                      value={column.config?.formula || ''}
                      validation={customFormulaValidation}
                    />
                    <Tooltip content="Update item values in the target board with this custom formula" position="right">
                      <div style={{ backgroundColor: '#ffffff' }}>
                        {column.config?.to_monday_is_quick_actions ? (
                          <Badge color={'primary' as any}>
                            <Button
                              rightIcon={TurnInto}
                              size="medium"
                              kind={column.config?.formula ? 'primary' : 'primary'}
                              disabled={!column.config?.formula}
                              onClick={async () => {
                                onSave();
                                createSignal('to_monday_open_modal');
                                setShowToMondayModal(true);
                              }}
                            >
                              Update
                            </Button>
                          </Badge>
                        ) : (
                          <>
                            <Button
                              rightIcon={TurnInto}
                              size="medium"
                              kind={column.config?.formula ? 'primary' : 'primary'}
                              disabled={!column.config?.formula}
                              onClick={async () => {
                                onSave();
                                createSignal('to_monday_open_modal');
                                setShowToMondayModal(true);
                              }}
                            >
                              Update
                            </Button>
                          </>
                        )}
                      </div>
                    </Tooltip>
                  </Flex>
                )}
                {selectedColumnTypeId === 'blank' && (
                  <div
                    style={{
                      pointerEvents: 'none',
                      userSelect: 'none',
                      flex: 1,
                      height: '38px',
                      minHeight: '30px',
                      backgroundColor: '#fff',
                      border: '1px solid #c3c6d4',
                      borderRadius: '4px',
                    }}
                  >
                    &nbsp;
                  </div>
                )}
              </div>
            </div>
          </div>

          {showDocumentation ? (
            <FormulaDocumentationModal
              show={showDocumentation}
              onClose={() => {
                setShowDocumentation(false);
              }}
            />
          ) : (
            <></>
          )}

          <div
            style={{
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Icon icon={MoveArrowRight} size={20} />
          </div>

          {column.config?.cell_bg_color ||
          column.config?.cell_font_bold ||
          column.config?.cell_font_cancelline ||
          column.config?.cell_font_color ||
          column.config?.cell_font_italic ||
          column.config?.cell_font_size ||
          column.config?.cell_font_type ||
          column.config?.cell_font_underline ||
          column.config?.custom_headline ||
          column.config?.cell_horizontal_alignment ? (
            <Badge color={'primary' as any}>
              <div
                className="column--box"
                style={{
                  width: '160px',
                  minWidth: '160px',
                  maxWidth: '160px',
                  borderColor: '#0073ea',
                }}
                onClick={() => {
                  setShowConfig(true);
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 4px 0 12px',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                    }}
                  >
                    <div
                      style={{
                        pointerEvents: 'none',
                        transform: 'rotate(90deg)',
                      }}
                    >
                      <Icon icon={Status} size={16} />
                    </div>
                    <div>Column {indexToAlpha(idx)}</div>
                  </div>
                  <div
                    style={{
                      margin: '4px 3px 0px auto',
                    }}
                  >
                    <IconButton
                      icon={SettingsIcon}
                      kind="tertiary"
                      size="xs"
                      onClick={() => {
                        setShowConfig(true);
                      }}
                      className="icon--button icon--button-grey"
                    />
                  </div>
                </div>
              </div>
            </Badge>
          ) : (
            <div
              className="column--box"
              style={{
                width: '160px',
                minWidth: '160px',
                maxWidth: '160px',
              }}
              onClick={() => {
                setShowConfig(true);
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 4px 0 12px',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                  }}
                >
                  <div
                    style={{
                      pointerEvents: 'none',
                      transform: 'rotate(90deg)',
                    }}
                  >
                    <Icon icon={Status} size={16} />
                  </div>
                  <div>Column {indexToAlpha(idx)}</div>
                </div>
                <div
                  style={{
                    margin: '4px 3px 0px auto',
                  }}
                >
                  <IconButton
                    icon={SettingsIcon}
                    kind="tertiary"
                    size="xs"
                    onClick={() => {
                      setShowConfig(true);
                    }}
                    className="icon--button icon--button-grey"
                  />
                </div>
              </div>
            </div>
          )}

          {showConfig && boardField ? (
            <ColumnConfigModal
              config={column.config || null}
              show={showConfig}
              columnName={indexToAlpha(idx)}
              fieldName={boardField ? boardField.label : ''}
              field={boardField}
              onClose={(newConfig) => {
                if (newConfig) {
                  onChange({
                    ...column,
                    config: newConfig,
                  });
                }
                setShowConfig(false);
              }}
            />
          ) : (
            <></>
          )}

          <div>
            <Flex gap="xs" align="center" direction="row">
              <IconButton
                icon={Delete}
                kind="tertiary"
                size="small"
                onClick={() => {
                  onRemove();
                }}
                className="icon--button-small"
              />
            </Flex>
          </div>
        </Flex>
      </div>
    </div>
  );
}

type FieldGroupProps = {
  boards: Board[];

  config: TemplateConfig;

  fieldGroup: FieldGroupType;

  template: Template;

  sheet: Sheet;

  onChange: (fieldGroup: FieldGroupType) => void;

  onRemove: () => void;

  onSave: () => void;
};

export function FieldGroup({ boards, fieldGroup, template, onRemove, onChange, sheet, onSave }: FieldGroupProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [moreButtonActive, setMoreButtonActive] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [showOptionExplanationModal, setShowOptionExplanationModal] = useState(false);
  const [showBoardDiagnoseModal, setShowBoardDiagnoseModal] = useState(false);
  const [showAdvancedSettingsModal, setShowAdvancedSettingsModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const board = boards.find((b) => fieldGroup.board_id === b.id);
  const fields = board ? getBoardFields(board) : [];
  const boardColumnFields = fields.filter((f) => !isBlankBoardField(f) && !isCustomFormulaBoardField(f));

  function handleDragEnd(event: any) {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = fieldGroup.columns.findIndex(({ id }) => id === active.id);
      const newIndex = fieldGroup.columns.findIndex(({ id }) => id === over.id);

      onChange({
        ...fieldGroup,
        columns: arrayMove(fieldGroup.columns, oldIndex, newIndex),
      });
    }
  }

  return (
    <>
      {showAdvancedSettingsModal ? (
        <FieldGroupAdvancedSettingsModal
          show={showAdvancedSettingsModal}
          fieldGroup={fieldGroup}
          board={board}
          onChange={(updatedFieldGroup) => {
            onChange(updatedFieldGroup);
          }}
          setShowBoardDiagnoseModal={setShowBoardDiagnoseModal}
          onClose={() => {
            setShowAdvancedSettingsModal(false);
          }}
        />
      ) : null}
      {showBoardDiagnoseModal ? (
        <BoardDiagnoseModal
          boardId={fieldGroup.board_id}
          mondayClient={mondayClient}
          onClose={() => {
            setShowBoardDiagnoseModal(false);
          }}
        />
      ) : null}
      {showFilters ? (
        <BoardRulesModal
          isOpen={showFilters}
          board={board!}
          inititalBoardFilters={fieldGroup.board_filters || {}}
          onClose={(newBoardFilters) => {
            if (newBoardFilters !== null && newBoardFilters !== undefined) {
              onChange({
                ...fieldGroup,
                board_filters: newBoardFilters,
              });
            }

            setShowFilters(false);
          }}
        />
      ) : null}
      <AppModal
        data-testid="option-explanation"
        show={showOptionExplanationModal}
        onClose={() => {
          setShowOptionExplanationModal(false);
        }}
        width="default"
        alertDialog={false}
      >
        <AppModalContent>
          <Box>
            <div style={{ marginBottom: '15px' }}>
              <Flex gap={12}>
                <h2>What are Dynamic Boards?</h2>
              </Flex>
              <p>
                If you have multiple boards with the same column structure, you typically need separate Spreadsheet Templates for each. To
                simplify, mark a board as a "Dynamic Board" in your Spreadsheet Template. During export, all board columns are then replaced
                with the current board's data.
              </p>
              <p>
                For example, if boards A and B share the same structure, you can create a Spreadsheet Template with board A marked as a
                "Dynamic board". When you set up an export automation in board B using this template, it will export the items from board B
                instead of board A.
              </p>
            </div>
          </Box>
        </AppModalContent>
        <AppModalFooter>
          <Flex gap={12} justify="end">
            <Button
              kind="tertiary"
              onClick={() => {
                setShowOptionExplanationModal(false);
              }}
            >
              Close
            </Button>
            <Button
              color="primary"
              onClick={async () => {
                //setShowOptionExplanationModal(false);
                window.open('https://getgorilla.app/products/spreadsheet-gorilla/dynamic-boards', '_blank');
              }}
            >
              Learn more
            </Button>
          </Flex>
        </AppModalFooter>
      </AppModal>
      <div id={`${fieldGroup.id}`}>
        <div>
          <div
            style={{
              position: 'relative',
            }}
          >
            <div
              style={{
                zIndex: 100,
                position: 'sticky',
                top: '73px',
                padding: '32px 32px 22px 32px',
                backgroundColor: '#f6f7fb',
                borderTopRightRadius: '8px',
              }}
            >
              <Flex>
                <div style={{ width: 'auto' }}>
                  <Flex align="center" gap={30}>
                    <Flex align="center" gap={10}>
                      <Tooltip content={board?.name || `Board: ${fieldGroup.board_id}`} position="right" zIndex={2000}>
                        <Avatar withoutBorder square size="medium" type="icon" icon={BoardIcon} customBackgroundColor="#323338" />
                      </Tooltip>
                      <Flex align="center" gap={10}>
                        <div style={{ maxWidth: '250px' }}>
                          <Heading
                            type="h3"
                            ellipsis={true}
                            //withoutTooltip={true}
                            style={{ pointerEvents: 'none' }}
                          >
                            {board?.name || `Board: ${fieldGroup.board_id}`}
                          </Heading>
                        </div>
                        {fieldGroup.disable ? (
                          <Tooltip content="This board will be skipped during export" position="right" zIndex={2000}>
                            <Label text="Disabled Board" size="medium" color="negative" />
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                        {/* 
                        {fieldGroup.disable_mirror_columns ? (
                          <Tooltip content="Mirror columns will be skipped during export" position="right" zIndex={2000}>
                            <Label text="Mirror Columns" size="medium" color="negative" />
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                          */}
                        {fieldGroup.from_context ? (
                          <Tooltip content="Click to learn more about this feature" position="right" zIndex={2000}>
                            <Label
                              text="Dynamic Board"
                              size="medium"
                              color="primary"
                              onClick={() => {
                                setShowOptionExplanationModal(true);
                              }}
                            />
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                        <IconButton
                          icon={Delete}
                          kind="tertiary"
                          size="small"
                          color="primary"
                          onClick={() => {
                            //onRemove();
                            setShowConfirmDeleteModal(true);
                          }}
                        />
                      </Flex>
                    </Flex>
                  </Flex>
                </div>

                <AppModal
                  data-testid="confirm-delete-fieldgroup-modal"
                  show={showConfirmDeleteModal}
                  onClose={() => {
                    setShowConfirmDeleteModal(false);
                  }}
                  width="default"
                  alertDialog={false}
                >
                  <AppModalContent>
                    <Box>
                      <div
                        style={{
                          marginTop: '10px',
                        }}
                      >
                        You are about to remove the{' '}
                        <span style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-block', transform: 'translate3d(0,4px,0)' }}>
                            <Icon icon={BoardIcon} size={20} />
                          </span>{' '}
                          <strong>{board?.name}</strong>
                        </span>{' '}
                        board from{' '}
                        <span style={{ whiteSpace: 'nowrap' }}>
                          <span style={{ display: 'inline-block', transform: 'translate3d(0,4px,0)' }}>
                            <Icon icon={Table} size={20} />
                          </span>{' '}
                          <strong>{sheet?.name}</strong>
                        </span>{' '}
                        in this spreadsheet template configuration. Are you sure to proceed?
                      </div>
                    </Box>
                  </AppModalContent>
                  <AppModalFooter>
                    <Flex gap={12} justify="end">
                      <Button
                        kind="tertiary"
                        onClick={() => {
                          setShowConfirmDeleteModal(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="negative"
                        onClick={async () => {
                          setShowConfirmDeleteModal(false);
                          onRemove();
                        }}
                      >
                        Remove
                      </Button>
                    </Flex>
                  </AppModalFooter>
                </AppModal>

                <div style={{ marginLeft: 'auto' }}>
                  <Flex gap={14}>
                    <>
                      {hasBoardFilters(board!, fieldGroup.board_filters) ? (
                        areBoardFiltersValid(board!, fieldGroup.board_filters) ? (
                          <Badge color={'primary' as any}>
                            <Button
                              leftIcon={Filter}
                              kind="secondary"
                              size="medium"
                              color="primary"
                              onClick={() => {
                                setShowFilters((prevVal) => !prevVal);
                              }}
                            >
                              Edit filters
                            </Button>
                          </Badge>
                        ) : (
                          <Button
                            leftIcon={Filter}
                            kind="primary"
                            size="medium"
                            color="negative"
                            onClick={() => {
                              setShowFilters((prevVal) => !prevVal);
                            }}
                          >
                            Invalid filters
                          </Button>
                        )
                      ) : (
                        <Button
                          leftIcon={Filter}
                          kind="secondary"
                          size="medium"
                          color={areBoardFiltersValid(board!, fieldGroup.board_filters) ? 'primary' : 'negative'}
                          onClick={() => {
                            setShowFilters((prevVal) => !prevVal);
                          }}
                        >
                          Add filters
                        </Button>
                      )}
                    </>
                    <Button
                      leftIcon={Status}
                      disabled={!fields.length}
                      kind="secondary"
                      size="medium"
                      className="add_column_button"
                      onClick={() => {
                        onChange({
                          ...fieldGroup,
                          columns: [
                            ...fieldGroup.columns,
                            {
                              path: boardColumnFields[0].path,
                              config: null,
                              id: createRandomId(),
                            },
                          ],
                        });
                      }}
                    >
                      Add column
                    </Button>
                    {fieldGroup.disable ||
                    fieldGroup.disable_mirror_columns ||
                    fieldGroup.from_context ||
                    fieldGroup.show_headlines ||
                    fieldGroup.formula_resolution === 'from_api' ? (
                      <Badge color={'primary' as any}>
                        <IconButton
                          active={moreButtonActive}
                          icon={SettingsKnobs}
                          size="medium"
                          kind="secondary"
                          tooltipContent="Advanced settings"
                          tooltipProps={{
                            zIndex: 2000,
                            position: 'bottom',
                          }}
                          onClick={() => {
                            setShowAdvancedSettingsModal(true);
                          }}
                        />
                      </Badge>
                    ) : (
                      <IconButton
                        active={moreButtonActive}
                        icon={SettingsKnobs}
                        size="medium"
                        kind="tertiary"
                        tooltipContent="Advanced settings"
                        tooltipProps={{
                          zIndex: 2000,
                          position: 'bottom',
                        }}
                        onClick={() => {
                          setShowAdvancedSettingsModal(true);
                        }}
                      />
                    )}
                  </Flex>
                </div>
              </Flex>
            </div>

            <div
              style={{
                zIndex: 98,
                position: 'sticky',
                top: '167px',
                margin: '0 24px',
                height: '10px',
                background: '#f6f7fb',
                boxShadow: '0px 4px 6px -4px rgba(0,0,0,0.4)',
              }}
            ></div>

            <div
              style={{
                position: 'relative',
                margin: '0 32px 42px 32px',
                paddingTop: '10px',
              }}
            >
              <div
                style={{
                  zIndex: 99,
                  position: 'absolute',
                  top: 0,
                  width: 'calc(100% + 64px)',
                  height: '10px',
                  backgroundColor: '#f6f7fb',
                  transform: 'translate3d(-32px,0,0)',
                }}
              ></div>
              {!board ? (
                <Box paddingTop="large" paddingBottom="large">
                  <div
                    style={{
                      textAlign: 'center',
                    }}
                  >
                    <p>The connected board was deleted or you lack permissions.</p>
                  </div>
                </Box>
              ) : (
                <>
                  <div style={{ marginTop: '6px' }}>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={fieldGroup.columns} strategy={verticalListSortingStrategy}>
                        {fieldGroup.columns.map((column, idx) => (
                          <Column
                            key={column.id}
                            onSave={onSave}
                            column={column}
                            idx={idx}
                            sheet={sheet}
                            fieldGroup={fieldGroup}
                            template={template}
                            board={board}
                            onChange={(newColumn) => {
                              onChange({
                                ...fieldGroup,
                                columns: fieldGroup.columns.map((c, i) => {
                                  if (i === idx) {
                                    return newColumn;
                                  }
                                  return c;
                                }),
                              });
                            }}
                            onRemove={() => {
                              onChange({
                                ...fieldGroup,
                                columns: fieldGroup.columns.filter((c, i) => i !== idx),
                              });
                            }}
                            fields={fields}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
